import androidx.compose.runtime.*
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*

external fun decodeURIComponent(str: String): String

/**
 * Extracts the access token from a Supabase invite/recovery email redirect (spec 9.9). Those links
 * land back on `site_url` with the session appended as a URL hash fragment, e.g.
 * `#access_token=...&type=invite&...` — there's no server round-trip involved, it's parsed client-side.
 */
fun parseAuthHashToken(hash: String): String? {
    if (hash.isBlank()) return null
    val params = hash.removePrefix("#").split("&").mapNotNull { pair ->
        val parts = pair.split("=", limit = 2)
        if (parts.size == 2) parts[0] to decodeURIComponent(parts[1]) else null
    }.toMap()
    return params["access_token"]?.takeIf { params["type"] in setOf("invite", "recovery") }
}

/** Shown instead of the normal app when the user arrives via an invite/recovery link (spec 9.9). */
@Composable
fun SetPasswordScreen(accessToken: String, onDone: (String) -> Unit) {
    var password by remember { mutableStateOf("") }
    var confirm by remember { mutableStateOf("") }
    var saving by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    val scope = rememberCoroutineScope()
    val mismatch = confirm.isNotEmpty() && password != confirm

    Div(attrs = { classes("page") }) {
        Div(attrs = { classes("card") }) {
            H2 { Text("Nastavit heslo") }
            P { Text("Zvol si heslo pro přihlášení do administrace Gambrinus Bowlingliga.") }
            Div {
                Label { Text("Nové heslo: ") }
                Input(type = InputType.Password) {
                    value(password)
                    attr("autofocus", "true")
                    onInput { password = it.value }
                }
            }
            Div {
                Label { Text("Heslo znovu: ") }
                Input(type = InputType.Password) { value(confirm); onInput { confirm = it.value } }
            }
            if (mismatch) Div { Text("Hesla se neshodují.") }
            Div(attrs = { classes("save-action") }) {
                Button(attrs = {
                    if (saving || password.length < 6 || mismatch) disabled()
                    onClick {
                        saving = true
                        error = null
                        scope.launch {
                            try {
                                Api.setPassword(accessToken, password)
                                onDone(accessToken)
                            } catch (e: Throwable) {
                                error = "Nastavení hesla selhalo: ${e.apiMessage()}"
                                saving = false
                            }
                        }
                    }
                }) { Text(if (saving) "Ukládám…" else "Uložit heslo") }
            }
            if (password.isNotEmpty() && password.length < 6) Div { Text("Heslo musí mít alespoň 6 znaků.") }
            error?.let { Div(attrs = { classes("editor-error") }) { Text(it) } }
        }
    }
}
